import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { fetchRest } from '@/common/api/fetcher'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT =
  'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA =
  'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH =
  'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'

type PageMatchType = 'all' | 'url_prefix' | 'url_exact' | 'url_regex' | 'product_id' | 'category_id'
type OpeningMode = 'fixed' | 'ai'

type OpeningQuestion = {
  key: string
  text: string
  choices: string[]
  allow_free_text: boolean
}

type OpeningSet = {
  id: number
  site_id: number
  campaign_id: number | null
  name: string
  page_match_type: PageMatchType
  page_match_value: string
  mode: OpeningMode
  ai_hint: string
  questions: OpeningQuestion[]
  priority: number
  enabled: boolean
  start_at: string | null
  end_at: string | null
  impression_count: number
  answer_count: number
  completion_count: number
  completion_rate: number
}

/** 新規作成時は id を持たないため、保存前のドラフトは Partial 相当で扱う */
type OpeningDraft = Omit<OpeningSet, 'id' | 'site_id' | 'impression_count' | 'answer_count' | 'completion_count' | 'completion_rate'>

type Campaign = { id: number; name: string }

type MatchEvaluation = {
  id: number
  name: string
  priority: number
  page_match_type: PageMatchType
  page_match_value: string
  matched: boolean
  reason: string | null
}

type MatchTestResult = {
  matched_set_id: number | null
  matched_name: string | null
  evaluated: MatchEvaluation[]
  campaign_targets_page: boolean
}

type PreviewResult = {
  questions: OpeningQuestion[]
  cached: boolean
  error: string | null
}

const MATCH_TYPES: { value: PageMatchType; label: string }[] = [
  { value: 'all', label: '全ページ' },
  { value: 'url_prefix', label: 'URL 前方一致' },
  { value: 'url_exact', label: 'URL 完全一致' },
  { value: 'url_regex', label: 'URL 正規表現' },
  { value: 'product_id', label: '商品ID' },
  { value: 'category_id', label: 'カテゴリID' },
]
const matchLabel = (v: PageMatchType) => MATCH_TYPES.find(m => m.value === v)?.label ?? v

const emptyQuestion = (): OpeningQuestion => ({ key: '', text: '', choices: [], allow_free_text: true })

/** オープニング質問（ページ条件つき・会話開始時にAIが投げかける設問） */
const AIPurchaseAssistOpenings = () => {
  const { siteId } = useParams()

  const [items, setItems] = useState<OpeningSet[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // null=一覧 / 'new'=新規 / number=編集
  const [editing, setEditing] = useState<'new' | number | null>(null)
  const [showTester, setShowTester] = useState(false)

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const d = await fetchRest<{ items?: OpeningSet[] }>(
        `/api/sites/${siteId}/purchase-assist/openings`
      )
      setItems(d.items ?? [])
    } catch (e) {
      setError(e instanceof Response ? `HTTP ${e.status}` : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
    fetchRest<{ items?: Campaign[] }>(`/api/sites/${siteId}/purchase-assist/campaigns`)
      .then(d => setCampaigns(d.items ?? []))
      .catch(() => {})
  }, [siteId])

  const remove = async (s: OpeningSet) => {
    if (!window.confirm(`「${s.name}」を削除します。よろしいですか？`)) return
    try {
      await fetchRest(`/api/sites/${siteId}/purchase-assist/openings/${s.id}`, { method: 'DELETE' })
      fetchAll()
    } catch {
      alert('削除に失敗しました')
    }
  }

  const toggleEnabled = async (s: OpeningSet) => {
    await fetchRest(`/api/sites/${siteId}/purchase-assist/openings/${s.id}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: !s.enabled }),
    }).catch(() => {})
    fetchAll()
  }

  if (editing !== null) {
    return (
      <OpeningEditor
        siteId={String(siteId)}
        campaigns={campaigns}
        setId={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          fetchAll()
        }}
      />
    )
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='❓ オープニング質問'
        description='会話の開始時にAIから投げかける設問を、ページ単位で設定します。合致するセットが無いページでは、AIプロファイルの質問例が使われます。'
      />

      <div className={`${CARD} mb-3 flex flex-wrap items-center gap-2 p-3`}>
        <Button color={ButtonColor.PRIMARY} onClick={() => setEditing('new')}>
          ＋ 新規作成
        </Button>
        <Button color={ButtonColor.SECONDARY} onClick={() => setShowTester(!showTester)}>
          🔍 ページ条件テスター
        </Button>
        <Button color={ButtonColor.SECONDARY} onClick={fetchAll}>
          ↻ 更新
        </Button>
        <div className='text-secondary ml-auto text-[11px]'>
          優先度が小さいセットが優先されます。合致するセットは1つだけ使われます。
        </div>
      </div>

      {showTester && <MatchTester siteId={String(siteId)} campaigns={campaigns} />}

      {error && <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>エラー: {error}</div>}

      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : items.length === 0 ? (
        <div className={`${CARD} text-tertiary p-8 text-center text-xs`}>
          オープニング質問がまだありません。作成するまでは、AIプロファイルの質問例が使われます。
        </div>
      ) : (
        <div className={`${CARD} overflow-x-auto p-0`}>
          <table className='w-full border-collapse text-xs'>
            <thead>
              <tr className='bg-surface-secondary'>
                <th className={`${TH} text-center`}>優先</th>
                <th className={TH}>名前</th>
                <th className={TH}>ページ条件</th>
                <th className={`${TH} text-center`}>モード</th>
                <th className={`${TH} text-center`}>設問数</th>
                <th className={`${TH} text-center`}>表示</th>
                <th className={`${TH} text-center`}>完了率</th>
                <th className={`${TH} text-center`}>状態</th>
                <th className={TH}>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className='border-b border-secondary'>
                  <td className={`${TD} text-secondary text-center font-mono`}>{s.priority}</td>
                  <td className={`${TD} font-semibold`}>{s.name}</td>
                  <td className={`${TD} text-secondary`}>
                    {matchLabel(s.page_match_type)}
                    {s.page_match_value && (
                      <div className='text-tertiary max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[10px]'>
                        {s.page_match_value}
                      </div>
                    )}
                  </td>
                  <td className={`${TD} text-center`}>
                    {s.mode === 'ai' ? (
                      <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[9px] font-bold'>AI生成</span>
                    ) : (
                      <span className='text-secondary text-[10px]'>固定</span>
                    )}
                  </td>
                  <td className={`${TD} text-center`}>{s.mode === 'ai' ? '-' : s.questions.length}</td>
                  <td className={`${TD} text-secondary text-center`}>{s.impression_count}</td>
                  <td className={`${TD} text-center`}>
                    {s.impression_count ? `${s.completion_rate}%` : <span className='text-tertiary'>-</span>}
                  </td>
                  <td className={`${TD} text-center`}>
                    <button
                      type='button'
                      onClick={() => toggleEnabled(s)}
                      className={`cursor-pointer rounded px-2 py-0.5 text-[10px] font-bold ${
                        s.enabled ? 'bg-surface-success text-success' : 'bg-surface-secondary text-tertiary'
                      }`}
                    >
                      {s.enabled ? '有効' : '無効'}
                    </button>
                  </td>
                  <td className={TD}>
                    <div className='flex gap-1.5'>
                      <Button color={ButtonColor.SECONDARY} onClick={() => setEditing(s.id)}>
                        編集
                      </Button>
                      <Button color={ButtonColor.CRITICAL} onClick={() => remove(s)}>
                        削除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Main>
  )
}

export default AIPurchaseAssistOpenings

/** ページ条件テスター：URLを入れて、どのセットが当たるかを保存前に確かめる */
const MatchTester = ({
  siteId,
  campaigns,
}: {
  siteId: string
  campaigns: Campaign[]
}) => {
  const [pageUrl, setPageUrl] = useState('')
  const [productId, setProductId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [result, setResult] = useState<MatchTestResult | null>(null)
  const [running, setRunning] = useState(false)

  const run = async () => {
    setRunning(true)
    try {
      const r = await fetchRest<MatchTestResult>(
        `/api/sites/${siteId}/purchase-assist/openings/match-test`,
        {
        method: 'POST',
        body: JSON.stringify({
          page_url: pageUrl || null,
          product_id: productId || null,
          category_id: categoryId || null,
          campaign_id: campaignId ? parseInt(campaignId) : null,
        }),
        }
      )
      setResult(r)
    } catch (e) {
      alert(`失敗: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className={`${CARD} mb-3 p-3`}>
      <div className='text-emphasis mb-2 text-xs font-bold'>🔍 ページ条件テスター</div>
      <div className='mb-2 grid grid-cols-2 gap-2'>
        <input
          value={pageUrl}
          onChange={e => setPageUrl(e.target.value)}
          placeholder='https://example.com/products/ABC'
          className={INPUT}
        />
        <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className={INPUT}>
          <option value=''>配信を指定しない</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input value={productId} onChange={e => setProductId(e.target.value)} placeholder='商品ID（任意）' className={INPUT} />
        <input value={categoryId} onChange={e => setCategoryId(e.target.value)} placeholder='カテゴリID（任意）' className={INPUT} />
      </div>
      <Button color={ButtonColor.PRIMARY} onClick={run} disabled={running}>
        {running ? '判定中...' : '判定する'}
      </Button>

      {result && (
        <div className='mt-2.5'>
          {!result.campaign_targets_page && (
            <div className='bg-surface-critical text-critical mb-2 rounded-lg p-2 text-[11px]'>
              ⚠ このページは配信の対象ページ条件に合致しません。ウィジェット自体が表示されません。
            </div>
          )}
          <div className='bg-surface-emphasis text-emphasis mb-2 rounded-lg p-2 text-xs'>
            {result.matched_set_id ? (
              <>
                適用されるセット: <strong>{result.matched_name}</strong>（#{result.matched_set_id}）
              </>
            ) : (
              <>合致するセットなし → AIプロファイルの質問例が使われます</>
            )}
          </div>
          <table className='w-full border-collapse text-[11px]'>
            <thead>
              <tr className='bg-surface-secondary'>
                <th className={TH}>優先</th>
                <th className={TH}>名前</th>
                <th className={TH}>条件</th>
                <th className={TH}>結果</th>
              </tr>
            </thead>
            <tbody>
              {result.evaluated.map(e => (
                <tr key={e.id} className='border-b border-secondary'>
                  <td className={`${TD} font-mono`}>{e.priority}</td>
                  <td className={TD}>{e.name}</td>
                  <td className={`${TD} text-secondary`}>{matchLabel(e.page_match_type)}</td>
                  <td className={TD}>
                    {e.matched ? <span className='text-success font-bold'>合致</span> : <span className='text-tertiary'>{e.reason}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** 作成・編集 */
const OpeningEditor = ({
  siteId,
  campaigns,
  setId,
  onClose,
  onSaved,
}: {
  siteId: string
  campaigns: Campaign[]
  setId: number | null
  onClose: () => void
  onSaved: () => void
}) => {
  const [draft, setDraft] = useState<OpeningDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewProductId, setPreviewProductId] = useState('')

  useEffect(() => {
    if (setId == null) {
      setDraft({
        name: '',
        campaign_id: null,
        page_match_type: 'all',
        page_match_value: '',
        mode: 'fixed',
        ai_hint: '',
        questions: [emptyQuestion()],
        priority: 100,
        enabled: true,
        start_at: null,
        end_at: null,
      })
      return
    }
    fetchRest<OpeningSet>(`/api/sites/${siteId}/purchase-assist/openings/${setId}`)
      .then(d => setDraft({ ...d, questions: d.questions.length ? d.questions : [emptyQuestion()] }))
  }, [setId])

  if (!draft) {
    return (
      <Main type={MainLayoutType.FULL}>
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      </Main>
    )
  }

  const update = <K extends keyof OpeningDraft>(k: K, v: OpeningDraft[K]) => setDraft({ ...draft, [k]: v })
  const updateQuestion = <K extends keyof OpeningQuestion>(i: number, k: K, v: OpeningQuestion[K]) => {
    const qs = draft.questions.slice()
    qs[i] = { ...qs[i], [k]: v }
    update('questions', qs)
  }

  const save = async () => {
    if (!draft.name.trim()) {
      alert('名前を入力してください')
      return
    }
    if (draft.mode === 'fixed' && !draft.questions.some(q => q.text.trim())) {
      alert('設問を1つ以上入力してください')
      return
    }
    setSaving(true)
    try {
      const body = {
        ...draft,
        questions: draft.questions
          .filter(q => q.text.trim())
          .map((q, i) => ({ ...q, key: (q.key || `q${i + 1}`).trim() })),
      }
      const url =
        setId == null
          ? `/api/sites/${siteId}/purchase-assist/openings`
          : `/api/sites/${siteId}/purchase-assist/openings/${setId}`
      await fetchRest(url, {
        method: setId == null ? 'POST' : 'PUT',
        body: JSON.stringify(body),
      })
      onSaved()
    } catch (e) {
      // fetchRest は失敗時に Response を throw する。サーバが返す理由を拾う
      const d = e instanceof Response ? await e.json().catch(() => ({})) : {}
      const message =
        d.detail ?? (e instanceof Response ? `HTTP ${e.status}` : String(e))
      alert(`保存に失敗しました: ${message}`)
    } finally {
      setSaving(false)
    }
  }

  const runPreview = async () => {
    if (setId == null) {
      alert('AI生成のプレビューは保存後に実行できます')
      return
    }
    setPreviewing(true)
    try {
      const r = await fetchRest<PreviewResult>(
        `/api/sites/${siteId}/purchase-assist/openings/${setId}/preview`,
        {
          method: 'POST',
          body: JSON.stringify({
            page_url: previewUrl || null,
            product_id: previewProductId || null,
            ai_hint: draft.ai_hint || null,
            force: true,
          }),
        }
      )
      setPreview(r)
    } catch (e) {
      alert(`失敗: ${e instanceof Response ? `HTTP ${e.status}` : String(e)}`)
    } finally {
      setPreviewing(false)
    }
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle title={setId == null ? '❓ オープニング質問の作成' : '❓ オープニング質問の編集'} />

      <div className={`${CARD} mb-3 flex flex-col gap-3.5 p-4`}>
        <div className='grid grid-cols-2 gap-3.5'>
          <Field label='名前' hint='運用上の識別名'>
            <input value={draft.name} onChange={e => update('name', e.target.value)} className={`${INPUT} w-full`} />
          </Field>
          <Field label='優先度' hint='小さいほど優先。合致するセットが複数あるとき1つだけ使われる'>
            <input
              type='number'
              value={draft.priority}
              onChange={e => update('priority', parseInt(e.target.value) || 100)}
              className={`${INPUT} w-full`}
            />
          </Field>
          <Field label='適用する配信' hint='未指定ならサイト内の全配信に適用'>
            <select
              value={draft.campaign_id ?? ''}
              onChange={e => update('campaign_id', e.target.value ? parseInt(e.target.value) : null)}
              className={`${INPUT} w-full`}
            >
              <option value=''>すべての配信</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label='モード' hint='固定＝書いた設問をそのまま出す / AI生成＝ページの文脈から作る'>
            <select value={draft.mode} onChange={e => update('mode', e.target.value as OpeningMode)} className={`${INPUT} w-full`}>
              <option value='fixed'>固定（推奨）</option>
              <option value='ai'>AI生成</option>
            </select>
          </Field>
        </div>

        <div className='grid grid-cols-2 gap-3.5'>
          <Field label='ページ条件'>
            <select
              value={draft.page_match_type}
              onChange={e => update('page_match_type', e.target.value as PageMatchType)}
              className={`${INPUT} w-full`}
            >
              {MATCH_TYPES.map(m => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label='掲載期間（任意）'>
            <div className='flex items-center gap-1.5'>
              <input
                type='date'
                value={draft.start_at?.slice(0, 10) ?? ''}
                onChange={e => update('start_at', e.target.value || null)}
                className={`${INPUT} w-full`}
              />
              <span className='text-secondary'>〜</span>
              <input
                type='date'
                value={draft.end_at?.slice(0, 10) ?? ''}
                onChange={e => update('end_at', e.target.value || null)}
                className={`${INPUT} w-full`}
              />
            </div>
          </Field>
        </div>

        {draft.page_match_type !== 'all' && (
          <Field
            label='条件の値（1行に1つ）'
            hint={
              draft.page_match_type.startsWith('url')
                ? 'URLを1行ずつ。いずれかに合致すれば適用されます'
                : 'IDを1行ずつ。設置タグから商品ID・カテゴリIDが渡っていないと判定できません'
            }
          >
            <textarea
              rows={4}
              value={draft.page_match_value}
              onChange={e => update('page_match_value', e.target.value)}
              placeholder={draft.page_match_type.startsWith('url') ? 'https://example.com/products/' : 'P0001'}
              className={`${TEXTAREA} font-mono text-[11px]`}
            />
          </Field>
        )}

        {draft.mode === 'ai' ? (
          <>
            <div className='bg-surface-warning text-warning rounded-lg p-2.5 text-[11px]'>
              AI生成はページの文脈から設問を作ります。生成結果は<strong>ページ単位で24時間キャッシュ</strong>され、
              生成に失敗した場合はAIプロファイルの質問例にフォールバックします。
              意図した設問を確実に出したい場合は「固定」を使ってください。
            </div>
            <Field label='必ず押さえる観点（任意）' hint='例：予算と用途を必ず聞く'>
              <textarea rows={2} value={draft.ai_hint} onChange={e => update('ai_hint', e.target.value)} className={TEXTAREA} />
            </Field>
            <div className='rounded-lg border border-secondary p-2.5'>
              <div className='text-emphasis mb-2 text-xs font-bold'>🧪 生成プレビュー</div>
              <div className='mb-2 grid grid-cols-2 gap-2'>
                <input value={previewUrl} onChange={e => setPreviewUrl(e.target.value)} placeholder='ページURL（任意）' className={INPUT} />
                <input
                  value={previewProductId}
                  onChange={e => setPreviewProductId(e.target.value)}
                  placeholder='商品ID（任意）'
                  className={INPUT}
                />
              </div>
              <Button color={ButtonColor.SECONDARY} onClick={runPreview} disabled={previewing}>
                {previewing ? '生成中...' : '生成してみる'}
              </Button>
              {preview && (
                <div className='mt-2 text-[11px]'>
                  {preview.error ? (
                    <div className='bg-surface-critical text-critical rounded p-2'>{preview.error}</div>
                  ) : (
                    preview.questions.map((q, i) => (
                      <div key={i} className='bg-surface-secondary mt-1 rounded p-2'>
                        <div className='text-primary font-semibold'>
                          {i + 1}. {q.text}
                        </div>
                        <div className='text-secondary mt-0.5'>{q.choices.join(' / ')}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className='flex flex-col gap-2.5'>
            <div className='text-emphasis text-xs font-bold'>設問（最大5問・上から順に提示）</div>
            {draft.questions.map((q, i) => (
              <div key={i} className='rounded-lg border border-secondary p-2.5'>
                <div className='mb-1.5 flex items-center gap-2'>
                  <span className='text-secondary text-[11px] font-bold'>{i + 1}問目</span>
                  {draft.questions.length > 1 && (
                    <button
                      type='button'
                      onClick={() => update('questions', draft.questions.filter((_, j) => j !== i))}
                      className='text-critical ml-auto cursor-pointer text-[11px]'
                    >
                      削除
                    </button>
                  )}
                </div>
                <input
                  value={q.text}
                  onChange={e => updateQuestion(i, 'text', e.target.value)}
                  placeholder='質問文（例：どなたが使いますか？）'
                  className={`${INPUT} mb-1.5 w-full`}
                />
                <input
                  value={q.choices.join(', ')}
                  onChange={e =>
                    updateQuestion(
                      i,
                      'choices',
                      e.target.value.split(',').map(s => s.trim()).filter(Boolean).slice(0, 4),
                    )
                  }
                  placeholder='選択肢（カンマ区切り・最大4件）'
                  className={`${INPUT} mb-1.5 w-full`}
                />
                <div className='flex items-center gap-3'>
                  <input
                    value={q.key}
                    onChange={e => updateQuestion(i, 'key', e.target.value)}
                    placeholder='識別子（集計用・英数字）'
                    className={`${INPUT} w-[220px]`}
                  />
                  <label className='text-primary flex items-center gap-1.5 text-xs'>
                    <input
                      type='checkbox'
                      checked={q.allow_free_text}
                      onChange={e => updateQuestion(i, 'allow_free_text', e.target.checked)}
                      className='accent-[var(--color-fill-brand)]'
                    />
                    自由入力でも答えられる
                  </label>
                </div>
              </div>
            ))}
            {draft.questions.length < 5 && (
              <Button color={ButtonColor.SECONDARY} onClick={() => update('questions', [...draft.questions, emptyQuestion()])}>
                ＋ 設問を追加
              </Button>
            )}
          </div>
        )}

        <label className='text-primary flex items-center gap-1.5 text-xs'>
          <input
            type='checkbox'
            checked={draft.enabled}
            onChange={e => update('enabled', e.target.checked)}
            className='accent-[var(--color-fill-brand)]'
          />
          有効にする
        </label>

        <div className='flex gap-1.5'>
          <Button color={ButtonColor.PRIMARY} onClick={save} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>
            キャンセル
          </Button>
        </div>
      </div>
    </Main>
  )
}

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className='text-secondary mb-1 block text-[11px] font-semibold'>
      {label}
      {hint && <span className='text-tertiary ml-1.5 font-normal'>— {hint}</span>}
    </label>
    {children}
  </div>
)
